import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * What a screen tells the frame about itself. Ours, and it has no counterpart
 * in the fleet: there every address is complete, so the frame reads everything
 * it needs off the route.
 *
 * There is exactly one of these, deliberately. A route knows it is a site's
 * page; only the screen knows *which* site — and, on that same screen, which
 * workspace the site is in, because `/sites/$siteId` does not carry it (design
 * note 0001 §2, and rightly: the API resolves membership from the site). Two
 * channels for those two facts would be two ways to tell the frame something,
 * and they would go out of step (review of #10).
 *
 * So: one object, published by the screen, merged over what the address holds.
 */
export interface PageFrame {
  /** What to call this screen, where its route only knows the kind. */
  title?: string;
  /** The workspace this screen is in, when the address does not say. */
  workspaceId?: string;
}

const PageFrameContext = createContext<{
  frame: PageFrame;
  setFrame: (frame: PageFrame) => void;
}>({ frame: {}, setFrame: () => {} });

export function PageFrameProvider({ children }: { children: ReactNode }) {
  const [frame, setFrame] = useState<PageFrame>({});
  const value = useMemo(() => ({ frame, setFrame }), [frame]);

  return <PageFrameContext.Provider value={value}>{children}</PageFrameContext.Provider>;
}

/** What the frame currently knows from the screen inside it. */
export function usePageFrameValue(): PageFrame {
  return useContext(PageFrameContext).frame;
}

/** Called by a screen that knows something about itself the address does not. */
export function usePageFrame({ title, workspaceId }: PageFrame) {
  const { setFrame } = useContext(PageFrameContext);

  useEffect(() => {
    setFrame({ title, workspaceId });
    // Cleared on the way out, so what the screen somebody just left published
    // cannot end up describing the one they arrived at.
    return () => setFrame({});
  }, [title, workspaceId, setFrame]);
}
